import type { FC, PropsWithChildren, ReactNode, SVGProps } from "react";
import { useMemo } from "react";
import { Card, InfoIcon } from "@ptsd/uikit";
import type { ViewProps } from "tamagui";
import { Text, useTheme, XStack, YStack } from "tamagui";

export interface OverviewCardProps extends ViewProps {
  title: string;
  subtitle?: ReactNode;
}
export const OverviewCard: FC<PropsWithChildren<OverviewCardProps>> = ({
  title,
  subtitle,
  children,
  ...rest
}) => {
  return (
    <Card
      shadow="md"
      paddingHorizontal={"$10"}
      paddingVertical={"$5"}
      {...rest}
    >
      <YStack flex={1} gap={"$5"} alignItems="center">
        <YStack gap={"$1"} alignItems="center" maxWidth={740}>
          <Card.Title>{title}</Card.Title>
          <Card.Subtitle textAlign="center">{subtitle}</Card.Subtitle>
        </YStack>
        {children}
      </YStack>
    </Card>
  );
};

const OverviewIconArrow: FC<SVGProps<SVGSVGElement>> = (props) => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
      <path
        d="M13.75 7.91675C11.4719 5.63869 10 4.16675 10 4.16675L6.25 7.91675"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M10 15.8335L10 4.5835"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const OverviewDiff: FC<{ diff: number | undefined | null }> = ({
  diff,
}) => {
  const theme = useTheme();

  const { color, isPositive } = useMemo(() => {
    const isPositive = diff && diff > 0;
    const color = isPositive
      ? theme["score-8"].get()
      : theme["error-500"].get();
    return { color, isPositive };
  }, [diff, theme]);

  if (!diff) return null;

  return (
    <XStack>
      <OverviewIconArrow
        color={color}
        transform={isPositive ? "rotate(0)" : "rotate(180)"}
      />
      <Text fontFamily={"$body"} lineHeight={"$2"} color={color}>
        {isPositive ? "+" : ""}
        {diff}
      </Text>
    </XStack>
  );
};

export const OverviewEmptyCard: FC<{ text: string; visible: boolean }> = ({
  text,
  visible,
}) => {
  if (!visible) return null;
  return (
    <Card
      width="100%"
      variant="borderless"
      borderRadius={"$2"}
      borderLeftWidth={"$1"}
      borderLeftColor={"$darkpink-500"}
      backgroundColor={"$lightpink-100"}
      paddingHorizontal={"$4"}
      paddingVertical={"$3"}
    >
      <XStack gap={"$2"} alignItems="center">
        <Text color={"$error-500"} fontSize={"$3"}>
          <InfoIcon width={32} height={32} />
        </Text>

        <Text
          fontFamily={"$body"}
          fontSize={"$4"}
          lineHeight={"$3"}
          fontWeight={"$6"}
          color={"$error-500"}
        >
          {text}
        </Text>
      </XStack>
    </Card>
  );
};
